/**
 * useDepartmentEvents — subscribes to /ws/departments/:deptId/events
 * and invalidates the matching TanStack Query caches on every NOTIFY.
 *
 * Reconnect strategy mirrors useArchitectRunStream + useBoardEvents.
 *
 * The deptId is the department's UUID (the WS channel uses the id-no-
 * dashes). The hook also invalidates the top-level list cache so the
 * 21-cell grid updates without a manual refetch.
 */
import type { DepartmentWsEvent } from '@chaos/types/department';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { env } from '../../env';
import { departmentsKeys } from '../api/departments';

function wsUrl(deptId: string): string {
  const base = env.VITE_API_BASE_URL;
  let url: URL;
  try {
    url = new URL(base, window.location.origin);
  } catch {
    url = new URL(window.location.origin);
  }
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/ws/departments/${deptId}/events`;
}

interface UseDepartmentEventsOptions {
  enabled?: boolean;
  url?: string;
  WebSocketCtor?: typeof WebSocket;
  /** Optional dept slug — when provided we invalidate the detail key too. */
  deptKey?: string;
}

export function useDepartmentEvents(
  deptId: string | undefined,
  opts: UseDepartmentEventsOptions = {},
): { connected: boolean } {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);
  const attemptRef = useRef(0);
  const [connected, setConnected] = useState(false);

  const enabled = opts.enabled !== false && Boolean(deptId);
  const overrideUrl = opts.url;
  const Ctor = opts.WebSocketCtor;
  const deptKey = opts.deptKey;

  useEffect(() => {
    if (!deptId || !enabled) return;

    const handleFrame = (raw: string): void => {
      let frame: DepartmentWsEvent;
      try {
        frame = JSON.parse(raw) as DepartmentWsEvent;
      } catch {
        return;
      }
      if (frame.kind === 'ping' || frame.kind === 'hello') return;
      // Coarse fan-out invalidation: any change to a department's queue,
      // handoffs, or escalations should refresh the detail page + list.
      qc.invalidateQueries({ queryKey: departmentsKeys.list() });
      if (deptKey) {
        qc.invalidateQueries({ queryKey: departmentsKeys.detail(deptKey) });
        qc.invalidateQueries({
          queryKey: [...departmentsKeys.all, 'queue', deptKey] as const,
        });
      }
    };

    const connect = (): void => {
      const Constructor = Ctor ?? WebSocket;
      const url = overrideUrl ?? wsUrl(deptId);
      const ws = new Constructor(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (msgEvent) => {
        const data =
          typeof msgEvent.data === 'string'
            ? msgEvent.data
            : new TextDecoder().decode(msgEvent.data as ArrayBuffer);
        handleFrame(data);
      };
      ws.onclose = () => {
        wsRef.current = null;
        setConnected(false);
        if (closedByUs.current) return;
        const delay = Math.min(30_000, 1_000 * 2 ** attemptRef.current);
        attemptRef.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // onclose follows.
      };
    };

    closedByUs.current = false;
    connect();

    return () => {
      closedByUs.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
      try {
        wsRef.current?.close();
      } catch {
        // Already closed.
      }
      wsRef.current = null;
    };
  }, [deptId, enabled, overrideUrl, Ctor, qc, deptKey]);

  return { connected };
}
